import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { combineLatest, Subscription } from "rxjs";
import moment from "moment";
import {
  AngularResizeElementDirection,
  AngularResizeElementEvent,
} from "../../../../directives/resize-element";
import { DashboardService } from "src/app/services/dashboard.service";
import { FilterService } from "src/app/services/filter.service";
import { DialogService } from "src/app/services/dialog.service";
import { NavigationService } from "src/app/services/navigation.service";
import { SessionService } from "src/app/services/session.service";
import {
  DpmStaticContext,
  DpmStaticService,
  HelpEntry,
  makeHelpLookup,
} from "../dpm-static/dpm-static.service";
import {
  CACHE_STATUS,
  CACHE_STATUS_META,
  CACHE_STATUS_TAB_ORDER,
  CacheGroup,
  CacheQuery,
  CacheVerdict,
  CacheVerdictStore,
  groupCacheRows,
  SqlCacheStaticService,
  worstStatus,
} from "./sql-cache-static.service";
// This screen's per-widget help text, shipped with the build rather than fetched
// — the same treatment DPM-Static and DEM-Static give theirs. See the note on
// HELP_INFO_JSON in dem-static.component.ts.
import HELP_INFO_JSON from "src/assets/help_info/sql-cache-static.json";
import { consumeStaticDashboardEntry } from "../static-dashboard-entry";

// ===========================================================================
// SQL Cache Review — the reviewer's queue for cached AI SQL
//
// Every question the assistant answered with SQL, grouped so one row of the list
// is one question and its (possibly several) cached queries, with the SQL, the
// cache_status the pipeline gave it and what it cost to answer.
//
// The queue reads; it does not run. A reviewer judges the SQL as cached and copies
// it out (the button on the code block) if they want to execute it somewhere they
// can see the data — so there is no result grid on this screen and nothing here
// posts a query anywhere.
//
// Built on DPM-Static / DEM-Static's structure on purpose: the same topnav, the
// same filter strip and badge dropdowns, the same right-hand filter panel, the
// same <icon-chart> panel header, the same per-dashboard persisted selection.
// A reviewer arriving from either of those screens should not have to relearn the
// chrome, and a fix to one filter bar is a fix to three.
//
// What it deliberately does NOT take from them:
//
//   * cross-filtering. DPM and DEM let a visual be ctrl+clicked into a filter
//     chip because their KPI figures, donut bands and tree rows are all views of
//     one aggregate. This screen's interaction is a queue: pick a question, read
//     its SQL, mark it. The status tabs are the narrowing, and a second selection
//     model over the top of them would only compete with them.
//   * dynamic (API-backed) filters. The queue is a cross-brand internal view, so
//     the only thing that scopes the request is the date range the endpoint takes
//     (from_date / to_date): one read per window, with every other filter applied
//     client-side over its response. That is what keeps the tabs and the KPI
//     counts in step without a refetch per tab.
//
// Reviewing DOES write: Approve and Reject post the chat API's cache_review
// action for the message (see SqlCacheStaticService.submitReview), which is the
// one thing on this screen that changes anything — and what it changes is
// `cache_status` itself.
//
// So the status and the verdict are ONE fact on this screen, not two: a query's
// verdict is read off its status (groupCacheRows) and a click moves both together
// (applyVerdict) — onto APPROVED or REJECTED, the tab named after the button, so a
// reviewer finds the question where they just filed it. The verdict is also
// confirmed in a snackbar, and only once the API has accepted it.
//
// FIVE statuses, not three. GOOD/BAD(READY FOR REVIEW) are verdicts recorded and
// waiting to be signed off; APPROVED/REJECTED are the same verdicts once settled.
// The queue used to render GOOD as "Approved" and BAD as "Rejected", which hid the
// difference and left a reviewer no way to see what still needed sign-off. Every
// one of the five now has its own KPI card, its own tab and its own badge, and
// CACHE_STATUS_TAB_ORDER is the single list all three are built from.
//
// That holds inside the detail pane too, which is where the collapse survived
// longest: only APPROVED and REJECTED fill a verdict button, count towards a row's
// "N/M approved" chip or put a tick on a query tab (CacheQuery.approved). A
// ready-for-review query reads as its own status in the verdict bar, with both
// buttons still unfilled — the sign-off it is waiting for is the click this screen
// exists to collect, and a bar that already said "Approved" was telling the
// reviewer the work was done.
//
// The status filter is a TAB: one status at a time, or All, which is what a fresh
// visit opens on — so nothing is filtered out on arrival and all five are on screen
// with their own counts. `statusTab` is the whole of that state, and the tab strip
// is the only control that writes it.
//
// The KPI row is a REPORT, not a second control: six read-only cards, one per
// status plus the total. ALL SIX are independent of the open tab — each counts
// across the current filters, so opening a tab narrows the list and leaves every
// figure above it exactly where it was. See buildKpis().
// ===========================================================================

/**
 * The palette the KPI figures, the status dots and the tab pills share.
 *
 * The five status entries are CACHE_STATUS_META's dots, named here so a card can be
 * coloured without reaching into the meta table for a status it is only counting —
 * and read FROM the meta table rather than restated, so a card and the tab beside
 * it cannot drift onto two greens. The two at the end are chrome, not statuses:
 * `ink` for the total (which is not a state), `usage` for the usage card's icon.
 */
const COLORS = {
  ink: "#101d42",
  pending: CACHE_STATUS_META[CACHE_STATUS.pending].dot,
  good: CACHE_STATUS_META[CACHE_STATUS.good].dot,
  bad: CACHE_STATUS_META[CACHE_STATUS.bad].dot,
  approved: CACHE_STATUS_META[CACHE_STATUS.approved].dot,
  rejected: CACHE_STATUS_META[CACHE_STATUS.rejected].dot,
  usage: "#1360df",
};

/** This screen's help lookup — see makeHelpLookup() in dpm-static.service.ts. */
const helpFor = makeHelpLookup(HELP_INFO_JSON as HelpEntry[]);

/**
 * The window a fresh visit asks the API for, ending today.
 *
 * A default rather than "everything": from_date / to_date are required by
 * api/chat-history/usage/get-detail, so some window has to be chosen before the
 * first request, and the queue is worked newest-first — a month is what a
 * reviewer is actually behind on. Widening it is the date chip, which refetches.
 */
const DEFAULT_RANGE_DAYS = 30;

/**
 * How far back the date chip will let a reviewer ask.
 *
 * A guard on an absurd request (a mis-drag to 1970 pulling the whole cache
 * through one response), not a statement about how much history exists — two
 * years is well past anything a review queue looks at.
 */
const RANGE_FLOOR_YEARS = 2;

/**
 * The KPI row: a total, then one card per status in CACHE_STATUS_TAB_ORDER.
 *
 * `key` reads the tally buildKpis() has already computed rather than filtering the
 * rows again per card, so the six figures are six reads of one pass over the data
 * and cannot disagree with each other.
 *
 * `status` is which cache state the card counts, and is how buildKpis() tallies
 * without an if-chain per status (see TALLY_KEY_BY_STATUS). It is NOT a control:
 * the cards are read-only, and the tab strip is the only thing that filters this
 * screen. The total card's is null — it is the row's sum rather than a state, so
 * there is no single status for it to count.
 *
 * The status cards carry the same names as the tabs and the badges
 * (CACHE_STATUS_META) rather than wordings of their own, and stand in the same
 * order (CACHE_STATUS_TAB_ORDER), so a figure and the tab that filters to it are
 * recognisably the same thing in the same place.
 *
 * The two ready-for-review cards share their icons with the settled cards they
 * correspond to, and are told apart by the muted colour: a tick is a tick whether
 * it has been signed off or not, and a fifth and sixth glyph would be two more
 * shapes to learn for a distinction the palette already draws.
 *
 * `help` is the `info_header` in assets/help_info/sql-cache-static.json, named
 * rather than derived from `heading`: an entry describes a card, and which entry
 * belongs to which card is the JSON's call, not a string transform's.
 */
const KPI_CARD_DEFS: {
  key: keyof CacheTally;
  status: string | null;
  heading: string;
  icon: string;
  color: string;
  help: string;
}[] = [
  {
    key: "total",
    status: null,
    heading: "Total queries cached",
    icon: "storage_circle",
    color: COLORS.ink,
    help: "Total queries cached",
  },
  {
    key: "pending",
    status: CACHE_STATUS.pending,
    heading: "No feedback",
    icon: "clock_circle",
    color: COLORS.pending,
    help: "No feedback",
  },
  {
    key: "good",
    status: CACHE_STATUS.good,
    heading: "Good (ready for review)",
    icon: "tickmark_circle",
    color: COLORS.good,
    help: "Good (ready for review)",
  },
  {
    key: "bad",
    status: CACHE_STATUS.bad,
    heading: "Bad (ready for review)",
    icon: "cross_circle",
    color: COLORS.bad,
    help: "Bad (ready for review)",
  },
  {
    key: "approved",
    status: CACHE_STATUS.approved,
    heading: "Approved",
    icon: "tickmark_circle",
    color: COLORS.approved,
    help: "Approved",
  },
  {
    key: "rejected",
    status: CACHE_STATUS.rejected,
    heading: "Rejected",
    icon: "cross_circle",
    color: COLORS.rejected,
    help: "Rejected",
  },
];

/**
 * Which tally field a query's status counts towards, derived from the card defs
 * rather than restated — a status that gains a card starts counting with it.
 *
 * Built once at module load instead of being searched per query: buildKpis() walks
 * every query in view on each filter change, and a linear scan of the card defs
 * inside that loop is six comparisons per query for an answer that never changes.
 */
const TALLY_KEY_BY_STATUS = new Map<string, keyof CacheTally>(
  KPI_CARD_DEFS.filter((c) => c.status).map((c) => [c.status as string, c.key])
);

/** The one pass over the filtered queries every KPI figure is read from. */
interface CacheTally {
  total: number;
  pending: number;
  good: number;
  bad: number;
  approved: number;
  rejected: number;
}

/**
 * A filter chip.
 *
 * `pick` is how the chip reaches its value on a group, which is what lets the
 * chip list name a display field (Brand) whose response column (`brand_key`) is
 * spelled differently — and what keeps applyFilters() from having to know any of
 * them. `key` stays the response column, because that is what the persisted
 * selection is filed under and a renamed label must not orphan a saved state.
 */
interface FilterDef {
  label: string;
  key: string;
  single: boolean;
  options: string[];
  applied: Set<string>;
  /** The working selection inside an open dropdown / ng-select, committed by
   *  Include or Exclude. */
  checked: string[];
  search: string;
  /** What a fresh visit opens on, and what Reset puts the chip back to. */
  defaultApplied: string[];
  pick: (g: CacheGroup) => string;
}

/** The chips, in strip order. All four are multi-select and all four default to
 *  every value found, so the queue opens on everything and narrowing is always
 *  the user's doing. */
const SQL_CACHE_FILTER_DEFS: {
  label: string;
  key: string;
  pick: (g: CacheGroup) => string;
}[] = [
  { label: "Project", key: "project", pick: (g) => g.project },
  { label: "Team", key: "team_name", pick: (g) => g.teamName },
  { label: "Brand", key: "brand_key", pick: (g) => g.brandKey },
  { label: "Schema", key: "schema_version", pick: (g) => g.schemaVersion },
];

@Component({
    selector: "app-sql-cache-static",
    templateUrl: "./sql-cache-static.component.html",
    styleUrls: ["./sql-cache-static.component.scss"],
    // Screen-scoped, like the other static dashboards: the request context these
    // build is this route's, and should not outlive the component or be shared
    // with another static screen. DpmStaticService is what builds that context
    // (buildContext) and holds this screen's bookmark / page-key state.
    providers: [DpmStaticService, SqlCacheStaticService],
    standalone: false
})
export class SqlCacheStaticComponent implements OnInit, AfterViewInit, OnDestroy {
  clientLogo: any;
  newColor: any;
  moduleName: any;
  clientKey: any;
  moduleKey: any;
  dashboardKey: any;

  // ---- data ----------------------------------------------------------------
  /** The response, ungrouped — kept so a reload is the only thing that refetches
   *  and every filter change re-derives from what is already in hand. */
  private rows: any[] = [];
  /** Every question in the response. */
  groups: CacheGroup[] = [];
  /** The chips + date + question search applied. The KPI row and the tab counts
   *  are read from here, so a tab always reports how many items it would show. */
  viewGroups: CacheGroup[] = [];
  /** The status tab applied on top — what the list actually renders. */
  listGroups: CacheGroup[] = [];

  private apiContext!: DpmStaticContext;

  // True from the first request until the screen has been rebuilt from the
  // response. Starts true so the panel opens as loading rather than flashing
  // "no data".
  loading = true;
  /** Nothing came back for this selection. Distinct from `loading` so an empty
   *  answer reads as an empty answer, not a spinner that never stops. */
  get noData(): boolean {
    return !this.loading && !this.groups.length;
  }

  // ---- header --------------------------------------------------------------
  dashboardTitle = "AI Cache Review";
  /**
   * The topnav's page-level Help icon. A static dashboard has no page config to
   * build this from, so it is stated here — see the same field on
   * DemStaticComponent for what each key does.
   *
   * `pageInfoVar` is left blank deliberately: it names the user-manual PDF topnav
   * resolves a `??um_url` placeholder against, and this screen has no manual yet.
   * The icon still opens and still answers with the page's help text.
   */
  readonly topnavHelpInfo = {
    pageInfoChecks: true,
    pageInfoVar: "",
    pageInfoApiKey: "",
    pageInfoWidgetKey: "",
  };

  /**
   * The filter bar's note.
   *
   * One sentence, true of every visit and every selection: the queue is the cache
   * for the window in the date chip (the one selection the read is scoped by), and
   * a verdict is filed against the message. There is nothing else it can be — the
   * screen has one source, so there is no second sentence and nothing to branch on.
   *
   * It says "recorded against the message" rather than naming the status the write
   * lands in. cache_review does rewrite cache_status (see the header note), but the
   * filter bar is not where a reviewer needs that mapping — the KPI cards' headings
   * and help text carry it, next to the figures it moves.
   */
  get noteText(): string {
    return "Reads chat_history for the selected dates — Approve / Reject is recorded against the message";
  }

  /**
   * What the list says when it has nothing to show.
   *
   * The two cases are not the same finding and a reviewer acts on them
   * differently. An empty response means the requested window holds no cached
   * queries — no chip can undo that, and the date chip is the control to reach
   * for, so the sentence names the window. Rows loaded but none surviving the
   * selection is the ordinary too-narrow-a-filter case.
   */
  get listEmptyText(): string {
    // Three readings, and the tab one comes first because it is the only empty list
    // a reviewer can produce with one click and undo with the next one. Left unnamed
    // it reads as "this window holds nothing cached", which is a different problem
    // with a different fix.
    if (this.statusTab && this.viewGroups.length) {
      return `No cached questions under "${this.statusLabel(
        this.statusTab
      )}" in this selection.`;
    }
    return this.groups.length === 0
      ? `No cached questions in ${this.dateRangeLabel || "this range"}.`
      : "No cached questions match these filters.";
  }

  // ---- filter chips --------------------------------------------------------
  filters: FilterDef[] = [];
  openKey: string | null = null;
  menuPos = { left: 0, top: 0 };
  /** Free-text match on the question. In the bar rather than in a chip because
   *  it has no option list behind it — there is nothing to drop down. */
  searchText = "";

  @ViewChild("badgeScroller") badgeScroller!: ElementRef<HTMLElement>;
  /** The strip's scroll arrows only appear when the chips actually overflow. */
  badgesOverflow = false;

  // ---- date range ----------------------------------------------------------
  dateMenuOpen = false;
  datePos = { left: 0, top: 0 };
  dateConfig: any;
  dateSelectedData: any;
  dateRangeLabel = "";
  /**
   * The requested window, YYYY-MM-DD and inclusive — from_date / to_date on the
   * read. Changing it is a new request, not a re-filter: see onCalendarApply().
   */
  fromDate = "";
  toDate = "";
  fromDateObj!: Date;
  toDateObj!: Date;
  /** The default window (DEFAULT_RANGE_DAYS back from today), i.e. what a fresh
   *  visit opens on and what Reset returns to — see applyDefaultRange(). */
  private defaultFrom = "";
  private defaultTo = "";

  // ---- right-side filter panel --------------------------------------------
  filterPanelOpen = false;
  panelWidth = 380;
  readonly AngularResizeElementDirection = AngularResizeElementDirection;

  // ---- KPI row -------------------------------------------------------------
  /**
   * Seeded with the constant chrome so the row is on screen from the first frame
   * and only the figures are pending — each card shows its own loader where its
   * number will go, so the layout does not shift when the values land.
   */
  kpis = KPI_CARD_DEFS.map((c) => ({
    ...c,
    help: helpFor(c.help),
    value: 0,
  }));
  /** The tally behind the cards. Also what the tabs' own counts are read
   *  against, so the row and the tabs describe one dataset. */
  tally: CacheTally = {
    total: 0,
    pending: 0,
    good: 0,
    bad: 0,
    approved: 0,
    rejected: 0,
  };
  /**
   * The usage card — how much work the queries in view took.
   *
   * Two figures in one card rather than two cards, the way DEM's Extracts card
   * groups its three: they answer one question (how heavy this slice of the cache
   * is) and neither is the headline.
   *
   * No spend figure. The response carries per-message and per-model `cost_usd`,
   * but this screen is a correctness review — a reviewer decides whether the SQL
   * is right, and nothing they do here is a spending decision. See
   * CacheModelUsage, where cost is dropped at the mapping.
   */
  usage = { totalTokens: 0, avgSeconds: 0 };
  readonly usageHelp = helpFor("Usage");
  readonly usageColor = COLORS.usage;

  /** The entry the shared card popover is showing. One field for the whole row
   *  rather than one per card: the cards render from an *ngFor, so a mat-menu
   *  inside it would be a menu per card. */
  cardHelp: HelpEntry | null = null;

  // ---- status tab (the tab strip, and the KPI cards that mirror it) ---------
  /**
   * The status in view, or null for All.
   *
   * ONE field, and both controls that set it — the pill strip and the KPI cards —
   * call selectTab() and read this back. Giving the row its own copy is the only
   * way the two could ever disagree, so it does not have one.
   *
   * Opens on All rather than on a status: a reviewer arriving at the queue should
   * see how much of it there is, and all five counts, before being put inside one
   * bucket of it. That is also the "no status filter on load" the screen promises.
   */
  statusTab: string | null = null;
  /** Every tab with its count, rebuilt whenever the filters change. */
  tabs: {
    key: string;
    label: string;
    dot: string;
    count: number;
  }[] = [];

  // ---- review panel --------------------------------------------------------
  readonly reviewTitle = "Cache review queue";
  readonly reviewHelp = helpFor("Cache review queue");
  reviewFullscreen = false;
  /** Stable array reference for icon-chart's [data] — the header holds the array
   *  it was given, so the export is refilled in place rather than replaced. */
  readonly reviewExcelRows: any[] = [];

  /**
   * The panel header, mirroring DEM's grid panel entry for entry so the icons a
   * reviewer already knows behave the same here.
   *
   * No `gridload_circle`: that icon is Expand All, and this panel has no tree to
   * open. No `sort` either — the list is newest-first, which is the order a queue
   * is worked in.
   */
  readonly reviewHeaderConfig: any[] = [
    {
      name: "Page Header",
      value: "page_header",
      add_header: true,
      header_name_isNeed: true,
      page_header_name: this.reviewTitle,
    },
    {
      name: "Icons On Right side",
      value: "icons",
      add_header: true,
      icon_selected: true,
      iconList: [
        { name: "info_circle", value: "info_circle.svg", activated: true },
        { name: "expand_circle", value: "expand_circle.svg", activated: true },
        {
          name: "dotmenu_circle",
          value: "dotmenu_circle.svg",
          activated: true,
          is_Event: true,
          event: [
            {
              name: "Excel",
              value: "excel",
              is_true: true,
              dispPlayName: "Export as Excel",
            },
            {
              name: "PNG",
              value: "png",
              is_true: true,
              dispPlayName: "Export as Image",
            },
            {
              name: "Copy to Clipboard",
              value: "copy_image",
              is_true: true,
              dispPlayName: "Copy to Clipboard",
            },
          ],
        },
      ],
    },
  ];

  /** icon-chart keys its expand / info state by item.config.widgetKey, so this
   *  has to be a value no other panel in the portal claims. */
  readonly reviewItem: any = {
    key: 100711,
    config: { report_heading: this.reviewTitle, widgetKey: 100711 },
  };

  /**
   * "Export as Excel": one row per cached query, i.e. the queue flattened.
   *
   * Per query rather than per question because the query is the review unit —
   * status, cost and approval are all its, and a question-level export would
   * have to pick one of its queries' statuses to report.
   */
  readonly reviewExcelConfig: any[] = [
    { header: "Question", key: "question", type: "string" },
    { header: "Project", key: "project", type: "string" },
    { header: "Brand", key: "brand_key", type: "string" },
    { header: "Team", key: "team_name", type: "string" },
    { header: "Schema", key: "schema_version", type: "string" },
    { header: "Cache Status", key: "cache_status", type: "string" },
    // The reviewer's verdict, as one column rather than two flags: a query is
    // approved, rejected, awaiting sign-off or ungraded, and two Y/N columns could
    // spell states that cannot exist.
    { header: "Review", key: "review", type: "string" },
    { header: "Ingested", key: "ingested", type: "string" },
    { header: "Total Tokens", key: "total_tokens", type: "number" },
    { header: "LLM Time (s)", key: "llm_time", type: "number" },
    { header: "DB Time (s)", key: "db_time", type: "number" },
    { header: "Message Timestamp", key: "message_timestamp", type: "string" },
    { header: "Conversation Id", key: "conversation_id", type: "string" },
    { header: "Message Id", key: "message_id", type: "string" },
    { header: "SQL", key: "sql_query", type: "string" },
  ];

  // ---- selection (which question / query the detail pane is showing) -------
  /** The selected question's id, or null for none. Survives a reload and a
   *  filter change while the question is still in the list — see syncSelection(). */
  selectedId: string | null = null;
  /** The selected query within it. Null falls through to the first one. */
  selectedQueryId: string | null = null;

  /** The message_id whose Copy button is currently showing its "Copied"
   *  confirmation. */
  copiedId: string | null = null;
  /** The message_id whose corrected SQL Copy button is showing confirmation. */
  copiedCorrectedId: string | null = null;
  private copyTimer: any = null;

  /**
   * "Approved — moved to the Good tab", for the seconds after a verdict takes a
   * row out of the open tab.
   *
   * The row leaving IS the feature (see applyVerdict), but on its own it is also
   * exactly what a failed write would look like: the question the reviewer was
   * reading is simply gone, and the detail pane with it. This is the line that
   * tells the two apart, and it names the tab so the row can be followed.
   *
   * Only raised when the row actually left. On the All tab, and on a question with
   * other queries still matching, nothing disappears and there is nothing to
   * explain.
   */
  movedNotice: string | null = null;
  private movedTimer: any = null;

  readonly statusMeta = CACHE_STATUS_META;

  // ---- persisted filter state (survives a refresh) ------------------------
  /** Guards saving until the first load has finished restoring, so seeding the
   *  defaults during init never overwrites a previously saved selection. */
  private stateReady = false;
  private savedState: any = null;
  private get storageKey(): string {
    return `sql-cache-static-filter-state:${this.dashboardKey || "default"}`;
  }
  /** This browser's record of the verdicts it has posted, by message_id. Its own
   *  storage key, and deliberately not part of the state above — see
   *  readVerdicts(). The backend is the authority; this is what keeps a click
   *  visible until the next read echoes it. */
  private verdicts: CacheVerdictStore = {};
  /** The message_id whose verdict is in flight, so both buttons on that query can
   *  be held and the one being posted can say so. One at a time: a verdict is a
   *  click on the query in front of the reviewer. */
  savingVerdictId: string | null = null;
  /** WHICH of the two is in flight, so the spinner goes on the button that was
   *  actually clicked while the other is merely held. Meaningless on its own —
   *  always read with `savingVerdictId`, via isSavingOp(). */
  savingVerdictOp: CacheVerdict | null = null;
  /** Why a verdict did not stick, by message_id. Kept per message rather than as
   *  one field so switching queries cannot show another query's failure. */
  verdictErrors: Record<string, string> = {};

  // ---- correction (approve with a replacement query) ----------------------
  /**
   * The message_id whose correction box is open, or null for none.
   *
   * One at a time, and keyed by message rather than a bare boolean: the pane can be
   * moved to another query while a box is open, and a shared flag would carry the
   * open editor — and the draft in it — onto SQL it was never typed against.
   */
  correctionFor: string | null = null;
  /** What is in the box. Held on the component rather than read off the DOM so the
   *  Submit button can be held while it is blank without a template query. */
  correctionDraft = "";
  /** The message_id whose correction is being posted. Separate from
   *  `savingVerdictOp` so the spinner goes on the box's own Submit button: the write
   *  IS an approve, and keying it only by operation would spin the Approve button in
   *  the bar instead — see isSavingOp(). */
  savingCorrectionId: string | null = null;
  /** Why a correction did not stick, by message_id. Its own map rather than
   *  `verdictErrors`, so the failure is reported inside the box the reviewer is
   *  still holding their query in rather than as a line under the verdict bar. */
  correctionErrors: Record<string, string> = {};

  private reqSubcription: Subscription[] = [];
  /** Set by ngOnDestroy so work scheduled on a timer can tell that the view it
   *  would refresh is gone. */
  private destroyed = false;

  constructor(
    private sessionService: SessionService,
    private router: ActivatedRoute,
    private dashboardService: DashboardService,
    private navigationService: NavigationService,
    private filterService: FilterService,
    private dpmService: DpmStaticService,
    private cacheService: SqlCacheStaticService,
    private dialogService: DialogService,
    private cdr: ChangeDetectorRef
  ) {
    this.reqSubcription.push(
      combineLatest([this.router.params, this.router.queryParams]).subscribe(
        ([p, params]: [any, any]) => {
          this.moduleName = p["module"] || "";
          const decrypted = this.navigationService.decryptData(params);
          this.clientKey = decrypted["cl_key"];
          this.moduleKey = decrypted["md_key"];
          this.dashboardKey = decrypted["ds_key"];
          this.dashboardService.moduleKey = this.moduleKey;
          // `hist_flg` is read and ignored on purpose. It travels on every static
          // route (the dashboard card's two buttons are the only thing that sets
          // it) but the review queue has no Live / History reading — there is one
          // cache, and the date chip is how a reviewer looks further back.
          //
          // markForCheck, NOT detectChanges: both route observables replay on
          // subscribe, so this runs synchronously inside the constructor, where
          // Angular is still in creation mode for this component and
          // detectChanges() throws.
          this.cdr.markForCheck();
        }
      )
    );
  }

  ngOnInit(): void {
    this.clientLogo = this.sessionService.getClintLogo();
    this.newColor = this.sessionService.getColorCodes();

    // The topnav's Refresh button pushes a bare `true` onto filterQuery — that is
    // the signal to re-run the read. Anything else on that subject is another
    // page's filter widget and is not ours to answer, so the value is checked
    // rather than just the notification.
    this.reqSubcription.push(
      this.filterService.filterQuery.subscribe((q: any) => {
        if (q === true && !this.loading) this.loadCacheData();
      })
    );

    // This screen's page key, published before the first request so everything
    // page-scoped sends the same one — the read itself (through buildContext) and
    // the topnav's Help icon, which is clickable from the first frame and would
    // otherwise post whatever page the portal last visited.
    this.dpmService.publishPageKey(this.dashboardKey);

    this.loadDashboard();
  }

  ngAfterViewInit(): void {
    this.updateBadgeOverflow();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    clearTimeout(this.copyTimer);
    clearTimeout(this.movedTimer);
    this.reqSubcription.forEach((s) => s.unsubscribe());
    // Stop describing this screen to the Bookmarks panel — the next page is not it.
    this.dpmService.clearBookmarkState();
  }

  // ============================================================ load sequence
  /**
   * Everything this screen shows, in dependency order:
   *
   *   1. build the request context from the portal's own state (route keys,
   *      client / employee session, signed-in user) — DpmStaticService;
   *   2. drop the persisted selection if a card click opened this screen;
   *   3. settle the date range, which the request is scoped by — the saved one if
   *      the last visit left one, the default window otherwise;
   *   4. read the approval marks, which the grouping needs in order to stamp
   *      each query;
   *   5. fetch the rows.
   *
   * Step 3 is why the persisted state is read here as well as in onDataLoaded():
   * the range is a request parameter, so the saved window has to be known BEFORE
   * the read, while the chips and the status tab can only be validated after it
   * (they are checked against the values the response actually holds).
   */
  private loadDashboard(): void {
    this.loading = true;
    // Name the filters before the request has even gone out, so the strip and the
    // panel say what this screen filters by for the whole of the load rather than
    // appearing to have no filters. Only on the first load — a refresh keeps the
    // chips it has, so their values do not blink away and come back.
    if (!this.filters.length) this.buildFilterSkeleton();
    this.apiContext = this.dpmService.buildContext({
      dashboardKey: this.dashboardKey,
      clientKey: this.clientKey,
      moduleKey: this.moduleKey,
    });
    // Arriving from this dashboard's own card means "start here", so the selection
    // the last visit left behind is dropped before it can be read. Consumed here,
    // so from this load on the user's own changes persist and a refresh restores
    // them exactly as before. Approval marks are NOT part of this — review
    // progress is not a filter.
    if (consumeStaticDashboardEntry(this.dashboardKey)) this.clearFilterState();
    this.savedState = this.readSavedState();
    this.initRequestRange();
    this.loadCacheData();
  }

  /** The rows for the requested window. Also the refresh path — see the
   *  filterQuery subscription in ngOnInit, and the date chip, which refetches
   *  because the window it changes IS the request. */
  private loadCacheData(): void {
    // Suspend persistence while the screen is rebuilt from the response, so the
    // intermediate state never overwrites what the user actually chose.
    this.stateReady = false;
    this.loading = true;
    // markForCheck, NOT detectChanges: the first call comes from ngOnInit, where
    // this component's own view has not been rendered yet. Marking is enough on
    // both paths — the first load is followed by the view's own first pass, and a
    // refresh is a click inside the zone, so the loaders paint on the same turn.
    this.cdr.markForCheck();

    this.reqSubcription.push(
      this.cacheService
        .loadCacheRows(this.apiContext, this.fromDate, this.toDate)
        .subscribe((rows) => {
          this.rows = rows || [];
          this.onDataLoaded();
        })
    );
  }

  /** Build the screen from whatever `rows` now holds. */
  private onDataLoaded(): void {
    // Read before the grouping, which weighs these against what the feed itself
    // says about each message — see groupCacheRows.
    this.verdicts = this.cacheService.readVerdicts(this.dashboardKey);
    this.groups = groupCacheRows(this.rows, this.verdicts);
    // Newest first: a review queue is worked from the top, and the most recently
    // cached query is the one most likely to still be wrong.
    this.groups.sort((a, b) => b.timestamp - a.timestamp);

    // Re-read on every load: the previous selection has just been persisted, so
    // this is what carries it across a refresh.
    this.savedState = this.readSavedState();
    this.buildFilters(); // options from the loaded rows
    // The date range is NOT re-derived here. It is what was just asked for — the
    // request went out with it — so deriving it back off the response would
    // ratchet the window in to whatever days happened to have rows in them, and
    // the next refresh would ask for that narrower window instead.
    this.restoreFilterState(); // override the defaults with any saved selection

    // Cleared before applyFilters(), so the panel comes out of the loading state
    // in the same change-detection pass that fills it.
    this.loading = false;
    // Before applyFilters(), not after: publishing on filterQuery is what makes
    // the topnav's Bookmarks / Notes icons live, and doing it first leaves the
    // header agreeing with the list under it.
    this.publishPanelContext();
    this.applyFilters();
    this.stateReady = true; // from here on, user actions persist
    this.cdr.detectChanges();
    this.updateBadgeOverflow();
  }

  /**
   * Switch the topnav's Bookmarks and Notes icons on and hand their panels the
   * context they answer with.
   *
   * Both icons render for any dashboard_key, but they stay greyed out until
   * topnav's `filterFlag` flips — which happens when FilterService publishes
   * `baseQuery` on `filterQuery`. A dynamic dashboard's filter widget does that;
   * this screen has no filter widget, so it publishes the same value at the same
   * point in its own load. `baseQuery` rather than `true`, because `true` is the
   * Refresh button's signal and publishing it here would loop.
   */
  private publishPanelContext(): void {
    this.dashboardService.dashboardKey = this.dashboardKey;
    // The notes panel reads this to point itself at a dashboard's notes, and
    // reads it with a bare Object.keys() — which throws on the undefined it
    // starts as if this screen is the first page of the session.
    this.dashboardService.dashboardDetailsOfNotes = {};
    if (!this.filterService.report_type) this.filterService.report_type = "D";
    if (!this.filterService.model) this.filterService.model = {};
    this.filterService.baseQuery = Object.assign(
      {},
      this.filterService.baseQuery || {},
      this.apiContext,
      { from_date: this.fromDate, to_date: this.toDate }
    );
    this.filterService.filterQuery.next(this.filterService.baseQuery);
  }

  // =================================================================== filters
  /**
   * The chips before anything has been fetched: every name from
   * SQL_CACHE_FILTER_DEFS, with no options yet.
   *
   * Without this the strip is empty until the response lands, so the screen
   * spends its whole load claiming it has no filters. A named chip renders from
   * the first frame instead and fills in when its values arrive.
   */
  private buildFilterSkeleton(): void {
    this.filters = SQL_CACHE_FILTER_DEFS.map((d) => ({
      label: d.label,
      key: d.key,
      single: false,
      options: [],
      applied: new Set<string>(),
      checked: [],
      search: "",
      defaultApplied: [],
      pick: d.pick,
    }));
  }

  /** The chips with their options, derived from the loaded questions. Every chip
   *  defaults to all of its values, so nothing is hidden until the user hides it. */
  private buildFilters(): void {
    this.filters = SQL_CACHE_FILTER_DEFS.map((d) => {
      const options = this.distinct(d.pick);
      return {
        label: d.label,
        key: d.key,
        single: false,
        options,
        applied: new Set(options),
        checked: [...options],
        search: "",
        defaultApplied: [...options],
        pick: d.pick,
      };
    });
  }

  /** The distinct values of one dimension across the loaded questions, in the
   *  order they were met. */
  private distinct(pick: (g: CacheGroup) => string): string[] {
    return [
      ...new Set(
        this.groups
          .map((g) => (pick(g) || "").trim())
          .filter((v) => v && v !== "null")
      ),
    ];
  }

  /** A filter with values to pick from. Everything that would act on a selection
   *  is gated on this: a valueless chip can only show its name. */
  hasOptions(f: FilterDef): boolean {
    return f.options.length > 0;
  }

  /** What the panel's select says when it is empty. A filter still waiting on the
   *  load reads as pending; one the finished load left empty reads as empty. */
  filterPlaceholder(f: FilterDef): string {
    if (this.hasOptions(f)) return "Select " + f.label;
    return this.loading ? "Loading…" : "No values";
  }

  /**
   * Narrow the questions by the chips, the date range and the question search,
   * then rebuild everything read off that set.
   *
   * The status tab is applied AFTER, in buildList(), so every KPI figure and every
   * tab count describes the same slice whichever tab is open — a tab that reported a
   * count filtered by itself would always read as its own total, and a card that read
   * 0 for every status but the open one would give a reviewer nothing to pick their
   * next tab from. Nothing above the list moves with the tab; see buildKpis().
   */
  applyFilters(): void {
    this.clearMovedNotice(); // it described the list this call is replacing
    const needle = this.searchText.trim().toLowerCase();
    this.viewGroups = this.groups.filter((g) => {
      // The window is the request's, so the response is already inside it and
      // these two are normally no-ops. Kept because they are not always: a question
      // is placed by its LATEST message (`day`), so a group whose newest query falls
      // outside the window belongs outside the list — and a row the endpoint returns
      // outside the range it was given is filtered here rather than trusted.
      if (this.fromDate && g.day && g.day < this.fromDate) return false;
      if (this.toDate && g.day && g.day > this.toDate) return false;
      if (needle && !g.promptText.toLowerCase().includes(needle)) return false;
      return this.filters.every((f) => {
        // A filter with no values of its own has nothing to say about a question —
        // an unloaded chip's empty `applied` set would otherwise match nothing and
        // blank the whole screen.
        if (!this.hasOptions(f)) return true;
        const v = (f.pick(g) || "").trim();
        // A question with no value for a dimension is never filtered out by it.
        if (!v || v === "null") return true;
        return f.applied.has(v);
      });
    });

    this.buildKpis();
    this.buildTabs();
    this.buildList();
    this.buildExcelRows();
    this.cdr.detectChanges();
    this.updateBadgeOverflow(); // the Reset chip changes the strip's width
  }

  /**
   * The KPI row and the usage card, from one pass over the queries in view.
   *
   * EVERY figure here — Total, the five status cards and the usage pair — counts
   * across everything the chips, the date range and the search left in view, and
   * NONE of them moves when a tab is opened. The row is a report on the selection,
   * and the tab is a control over the list below it; a card that dropped to 0, or a
   * total that shrank to the open pill's count, would be hiding the very numbers a
   * reviewer picks their next tab from. So the whole row holds still while the queue
   * underneath it narrows.
   *
   * That makes `statusTab` deliberately absent from this method: nothing it computes
   * depends on the open tab, which is why selectTab() does not call it. What DOES
   * move a figure is a filter change (applyFilters) or a verdict (applyVerdict) —
   * both of which change what is in view.
   *
   * All six come off one pass, so the cards can never disagree about the data, and
   * Total is the sum of the five statuses by construction rather than by coincidence.
   */
  private buildKpis(): void {
    const tally: CacheTally = {
      total: 0,
      pending: 0,
      good: 0,
      bad: 0,
      approved: 0,
      rejected: 0,
    };
    let totalTokens = 0;
    let seconds = 0;

    this.viewGroups.forEach((g) =>
      g.queries.forEach((q) => {
        // Per-status, over everything in view. Off the card defs' own lookup rather
        // than an if-chain per status, so a status that gains a card starts counting
        // without a branch being added here. A status with no card — one the backend
        // added, which buildTabs() still gives a tab — simply has nothing to count.
        const key = TALLY_KEY_BY_STATUS.get(q.status);
        if (key) tally[key]++;

        // Total and usage, over the same set — the open tab is not consulted here.
        tally.total++;
        totalTokens += q.totalTokens;
        // The wall-clock a message actually took: the model call and the query
        // behind it are sequential, so they add rather than max.
        seconds += q.llmTime + q.dbTime;
      })
    );

    this.tally = tally;
    this.kpis.forEach((k) => (k.value = tally[k.key]));
    this.usage = {
      totalTokens,
      avgSeconds: tally.total ? seconds / tally.total : 0,
    };
  }

  /**
   * The tabs and their counts. A question counts towards a status when ANY of its
   * queries is in it, which is what makes the counts add up to more than the list
   * length on a queue holding mixed questions — and is the honest answer, because
   * such a question does belong under both.
   *
   * ALL FIVE statuses always get a tab, whether or not the current slice holds one.
   * The strip is the status filter's full vocabulary, not a report on this response:
   * a pill that came and went as the chips moved would move every pill after it
   * sideways under the reviewer's cursor, and the KPI row above — which always shows
   * six cards — would be offering a status the strip had dropped. A tab with nothing
   * behind it reads 0, which is a fact about the slice and not a gap in the control.
   */
  private buildTabs(): void {
    // Anything the response holds that CACHE_STATUS_TAB_ORDER does not name — a
    // status the backend added — is appended rather than dropped, so it gets a tab
    // of its own instead of being invisible. The five known ones lead, in queue
    // order (which is not the worst-first order a badge is picked in).
    const held = new Set<string>();
    this.viewGroups.forEach((g) =>
      g.queries.forEach((q) => held.add(q.status))
    );
    const extra = [...held].filter((s) => !CACHE_STATUS_TAB_ORDER.includes(s));
    const keys = [...CACHE_STATUS_TAB_ORDER, ...extra];

    this.tabs = keys.map((status) => ({
      key: status,
      // The short form: these are pills in a segmented track, and a segment
      // carrying the raw cache_status would stop being a segment. See
      // CACHE_STATUS_META.
      label: CACHE_STATUS_META[status]?.short || status,
      dot: CACHE_STATUS_META[status]?.dot || COLORS.pending,
      // Counted across everything in view, NOT under the open tab — an unopened
      // pill still has to say what opening it would bring.
      count: this.viewGroups.filter((g) =>
        g.queries.some((q) => q.status === status)
      ).length,
    }));
  }

  /**
   * The list, i.e. the filtered questions with the status tab applied.
   *
   * A question qualifies for a status tab when ANY of its queries is in that status,
   * so a question with one rejected attempt and one approved one is listed under
   * both — which is the honest answer, because it genuinely holds both.
   *
   * Each row is then stamped with the status it is being listed UNDER, which is the
   * point of the tab. Reading it off `worstStatus` instead had a row under the
   * Approved tab badged "Rejected", which is the tab contradicting the list it
   * produced. See CacheGroup.listedStatus.
   *
   * Stamped here rather than resolved in the template: the row markup renders per
   * question on every change-detection pass, and this depends only on the tab and
   * the queries, both of which settle right here.
   */
  private buildList(): void {
    const tab = this.statusTab;
    this.listGroups = tab
      ? this.viewGroups.filter((g) => g.queries.some((q) => q.status === tab))
      : this.viewGroups;

    this.listGroups.forEach((g) => {
      const matches = tab
        ? g.queries.filter((q) => q.status === tab).length
        : g.queries.length;
      // The guard matters on a row that somehow reached here with no match: it
      // keeps its honest worst-status badge rather than claiming the tab's.
      g.listedStatus = tab && matches ? tab : g.worstStatus;
      g.listedMatches = matches;
    });

    this.syncSelection();
  }

  /**
   * Keep the detail pane pointing at something the list still shows.
   *
   * A filter or a tab change can take the selected question out of the list, and
   * leaving it selected would show a detail pane for a row the user cannot see.
   * The selection is only cleared, never moved to another question — silently
   * selecting a neighbour would have the reviewer marking a query they did not
   * choose.
   */
  private syncSelection(): void {
    if (!this.selectedId) return;
    const stillThere = this.listGroups.some(
      (g) => g.questionId === this.selectedId
    );
    if (!stillThere) {
      this.selectedId = null;
      this.selectedQueryId = null;
    }
  }

  /**
   * Open a status tab, or All (null).
   *
   * The tab strip is the only caller — the KPI cards report, they do not filter.
   *
   * Re-clicking the open tab does nothing, the way a tab does. Getting back to All
   * is the All pill, which is on screen the whole time.
   */
  selectTab(key: string | null): void {
    const next = key || null;
    if (this.statusTab === next) return;
    this.clearMovedNotice(); // it named the tab being left
    this.cancelCorrection();
    this.statusTab = next;
    // Drop the explicit query pick, so the open question falls back through
    // activeQuery() to a query in the newly selected status. Switching to Bad while
    // looking at a question's good attempt should show the bad one — that is what
    // the tab was asked for.
    this.selectedQueryId = null;
    // buildList() and nothing else. Neither buildKpis() nor buildTabs() is re-run:
    // every figure in the KPI row and every count on the pills is read against the
    // whole view, so a tab change cannot move any of them — the tab only narrows the
    // list. Recomputing them here would be work that must produce what is already on
    // screen, and any figure it did move would be a bug.
    this.buildList();
    this.saveFilterState();
    this.cdr.detectChanges();
  }

  onSearchChange(): void {
    this.applyFilters();
    this.saveFilterState();
  }

  // ---- badge dropdown ------------------------------------------------------
  toggleDropdown(f: FilterDef, ev: MouseEvent): void {
    ev.stopPropagation();
    // Nothing loaded yet: the chip is there to name the filter, and there is no
    // list to open behind it.
    if (!this.hasOptions(f)) return;
    if (this.openKey === f.key) {
      this.openKey = null;
      return;
    }
    // Seed the working selection from what is currently applied.
    f.checked = [...f.applied];
    f.search = "";
    // A fixed menu placed under the trigger, so it is not clipped by the
    // horizontally-scrolling badge strip.
    const el = (ev.currentTarget as HTMLElement) || null;
    if (el) {
      const r = el.getBoundingClientRect();
      const width = 300;
      this.menuPos = {
        left: Math.min(r.left, window.innerWidth - width - 12),
        top: r.bottom + 4,
      };
    }
    this.openKey = f.key;
  }

  closeDropdown(): void {
    this.openKey = null;
  }

  get openFilter(): FilterDef | undefined {
    return this.filters.find((f) => f.key === this.openKey);
  }

  filteredOptions(f: FilterDef): string[] {
    const s = f.search.trim().toLowerCase();
    return s ? f.options.filter((o) => o.toLowerCase().includes(s)) : f.options;
  }

  isChecked(f: FilterDef, opt: string): boolean {
    return f.checked.includes(opt);
  }

  toggleOption(f: FilterDef, opt: string, ev?: Event): void {
    ev?.stopPropagation();
    f.checked = f.checked.includes(opt)
      ? f.checked.filter((x) => x !== opt)
      : [...f.checked, opt];
  }

  allChecked(f: FilterDef): boolean {
    const visible = this.filteredOptions(f);
    return visible.length > 0 && visible.every((o) => f.checked.includes(o));
  }

  toggleSelectAll(f: FilterDef, ev?: Event): void {
    ev?.stopPropagation();
    const visible = this.filteredOptions(f);
    if (this.allChecked(f)) {
      f.checked = f.checked.filter((o) => !visible.includes(o));
    } else {
      f.checked = [...new Set([...f.checked, ...visible])];
    }
  }

  clearChecked(f: FilterDef, ev?: Event): void {
    ev?.stopPropagation();
    f.checked = [];
  }

  canInclude(f: FilterDef): boolean {
    return f.checked.length > 0;
  }
  canExclude(f: FilterDef): boolean {
    return f.checked.length > 0 && f.checked.length < f.options.length;
  }

  applyInclude(f: FilterDef, sc?: { close?: () => void }): void {
    f.applied = new Set(f.checked); // show only the checked values
    this.openKey = null;
    this.applyFilters();
    this.saveFilterState();
    sc?.close?.();
  }

  applyExclude(f: FilterDef, sc?: { close?: () => void }): void {
    // show everything except the checked values
    f.applied = new Set(f.options.filter((o) => !f.checked.includes(o)));
    this.openKey = null;
    this.applyFilters();
    this.saveFilterState();
    sc?.close?.();
  }

  /** Closing an ng-select without applying reverts the working selection to what
   *  is applied — the same as the reference multi-select's onClose. */
  onSelectClose(f: FilterDef): void {
    f.checked = [...f.applied];
  }

  /** Is this chip off its default? Drives the chip's count colour and, through
   *  anyPartial, the Reset chip. */
  isPartial(f: FilterDef): boolean {
    if (!this.hasOptions(f)) return false;
    return !this.sameSelection([...f.applied], f.defaultApplied);
  }

  private sameSelection(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    const bs = new Set(b);
    return a.every((v) => bs.has(v));
  }

  /**
   * Is the screen off its defaults? Drives the Reset chip, so it appears exactly
   * when there is something for it to undo.
   *
   * The status tab is deliberately not in here: it is a view selector rendered as its
   * own visible row, with an All pill that undoes it, so a Reset chip offering to
   * clear it would be a second control for something already obvious. Everything else a reviewer can narrow by — the chips,
   * the date range and the question search — has no such affordance and is included.
   */
  get anyPartial(): boolean {
    return (
      this.filters.some((f) => this.isPartial(f)) ||
      this.dateChanged ||
      !!this.searchText.trim()
    );
  }

  /** Back to the state a fresh visit opens on: every chip on all of its values,
   *  the date range on the default window, and no question search. */
  resetFilters(): void {
    this.filters.forEach((f) => {
      f.applied = new Set(f.defaultApplied);
      f.checked = [...f.applied];
    });
    this.searchText = "";
    this.openKey = null;
    this.dateMenuOpen = false;
    // Whether this is a refetch depends on the range: Reset only asks the API
    // again when the window it is putting back is not the one already loaded.
    const rangeWasChanged = this.dateChanged;
    this.applyDefaultRange();
    this.saveFilterState();
    if (rangeWasChanged) {
      this.loadCacheData();
      return; // the response rebuilds the screen — see onDataLoaded()
    }
    this.applyFilters();
  }

  scrollBadges(dir: number): void {
    this.badgeScroller?.nativeElement?.scrollBy({
      left: dir * 220,
      behavior: "smooth",
    });
  }

  /** Show the scroll arrows only when the chips overflow the strip. Deferred
   *  because the widths are only real after the pass that renders them. */
  private updateBadgeOverflow(): void {
    setTimeout(() => {
      if (this.destroyed) return;
      const el = this.badgeScroller?.nativeElement;
      this.badgesOverflow = el ? el.scrollWidth > el.clientWidth + 2 : false;
    });
  }

  // ---- right-side filter panel --------------------------------------------
  toggleFilterPanel(): void {
    this.filterPanelOpen = !this.filterPanelOpen;
    this.openKey = null; // don't leave a badge dropdown open behind the panel
    if (this.filterPanelOpen) {
      // seed each ng-select's working selection from what is applied
      this.filters.forEach((f) => (f.checked = [...f.applied]));
    }
  }

  closeFilterPanel(): void {
    this.filterPanelOpen = false;
  }

  /** Drag the panel's left edge to resize it — same as app-filter. */
  onResizeSide(evt: AngularResizeElementEvent): void {
    this.panelWidth = evt.currentWidthValue;
  }

  /** ng-select header search: filter the ng-select's own item list. */
  onFilterText(ev: Event): string {
    return (ev.target as HTMLInputElement).value;
  }

  // ==================================================================== dates
  /**
   * The window the first request goes out with, settled before the read.
   *
   * The last visit's window if there is one — a reviewer who left the queue
   * looking at May comes back to May, and a refresh must not silently re-ask for
   * a different month than the chip says. The default window otherwise.
   *
   * Called once per visit, from loadDashboard() and ahead of the request. A
   * refetch does NOT come back through here: the range it is being run for is
   * already on the component.
   */
  private initRequestRange(): void {
    // The default first, so defaultFrom / defaultTo describe the same window on
    // every visit — they are what Reset returns to and what the Reset chip's
    // `dateChanged` is measured against, and a saved range must not redefine
    // "default" to itself.
    this.applyDefaultRange();

    const saved = this.savedState;
    const from = typeof saved?.fromDate === "string" ? saved.fromDate : "";
    const to = typeof saved?.toDate === "string" ? saved.toDate : "";
    // Both, and in order, or the entry is ignored: this is parsed localStorage,
    // and half a range would be a request for a window nobody chose.
    if (from && to && from <= to) {
      // Parsed through moment's format, NOT new Date(iso): the bare constructor
      // reads a YYYY-MM-DD string as UTC midnight, which commitDates() then
      // formats back in local time — one day earlier everywhere west of UTC, so a
      // refresh would quietly walk the saved window backwards.
      this.fromDateObj = moment(from, "YYYY-MM-DD").toDate();
      this.toDateObj = moment(to, "YYYY-MM-DD").toDate();
      this.commitDates();
    }
  }

  /**
   * The date range back to the default window — DEFAULT_RANGE_DAYS back from
   * today.
   *
   * Deliberately no kpi/get-timecycle-date call: that endpoint answers for a
   * configured dashboard's data period, and this screen's window is a request
   * parameter a reviewer sets, not a property of a report. Asking would introduce
   * a second, possibly disagreeing, answer.
   */
  private applyDefaultRange(): void {
    this.fromDateObj = moment().subtract(DEFAULT_RANGE_DAYS, "days").toDate();
    this.toDateObj = moment().toDate();
    this.commitDates();
    this.defaultFrom = this.fromDate;
    this.defaultTo = this.toDate;
  }

  /** True once the user has moved the range off the default window. */
  get dateChanged(): boolean {
    return this.fromDate !== this.defaultFrom || this.toDate !== this.defaultTo;
  }

  /**
   * fromDate / toDate and the chip's label, off the two Date objects.
   *
   * Formatting only — no fetch and no re-filter. The range is a request
   * parameter, so who committed it decides what happens next: the initial load
   * fetches once for the whole sequence, and the two user paths (onCalendarApply,
   * resetFilters) refetch for the window they just set. Committing on its own
   * during the pre-request setup must touch neither, because the view it would
   * re-render does not exist yet.
   */
  private commitDates(): void {
    if (this.fromDateObj)
      this.fromDate = moment(this.fromDateObj).format("YYYY-MM-DD");
    if (this.toDateObj)
      this.toDate = moment(this.toDateObj).format("YYYY-MM-DD");
    // MM/DD/YY, matching the date chip on the other two static screens.
    const fmt = (iso: string) => {
      const [y, m, d] = iso.split("-");
      return `${m}/${d}/${y.slice(2)}`;
    };
    this.dateRangeLabel =
      this.fromDate && this.toDate
        ? `${fmt(this.fromDate)} - ${fmt(this.toDate)}`
        : "All dates";
  }

  openDateMenu(ev: MouseEvent): void {
    ev.stopPropagation();
    if (this.dateMenuOpen) {
      this.dateMenuOpen = false;
      return;
    }
    this.closeDropdown(); // don't leave a badge dropdown open behind it
    this.buildDateCalendar();
    const el = ev.currentTarget as HTMLElement;
    if (el) {
      const r = el.getBoundingClientRect();
      const width = 580; // the dual calendar (from + to) is wide
      this.datePos = {
        left: Math.max(8, Math.min(r.left, window.innerWidth - width - 12)),
        top: r.bottom + 4,
      };
    }
    this.dateMenuOpen = true;
  }

  closeDateMenu(): void {
    this.dateMenuOpen = false;
  }

  /**
   * The picker's bounds.
   *
   * NOT the loaded rows' period, which is what a client-side date filter would
   * clamp to: the range is what the read asks for, so a picker limited to the
   * window already on screen could only ever narrow it and a reviewer could never
   * look further back. Open as far as RANGE_FLOOR_YEARS, and no further forward
   * than today — the cache has no future in it.
   */
  private buildDateCalendar(): void {
    this.dateConfig = {
      selectedType: "D",
      option: {
        minDate: moment().subtract(RANGE_FLOOR_YEARS, "years").toDate(),
        maxDate: moment().toDate(),
        isRestriction: "no",
        compulsaryFilter: true,
      },
    };
    this.dateSelectedData = {
      from_date: this.fromDateObj || new Date(this.fromDate),
      to_date: this.toDateObj || new Date(this.toDate),
    };
  }

  /**
   * A new window: persisted and read again.
   *
   * The one filter on this screen that refetches, because from_date / to_date are
   * the request — everything else narrows the response already in hand. A range
   * that comes back identical (the picker reopened and applied unchanged) is not
   * re-requested.
   */
  onCalendarApply(ev: { from_date: Date; to_date: Date }): void {
    const wasFrom = this.fromDate;
    const wasTo = this.toDate;
    if (ev?.from_date) this.fromDateObj = new Date(ev.from_date);
    if (ev?.to_date) this.toDateObj = new Date(ev.to_date);
    this.dateMenuOpen = false;
    this.commitDates();
    if (this.fromDate === wasFrom && this.toDate === wasTo) return;
    this.saveFilterState();
    this.loadCacheData();
  }

  // ============================================================== review pane
  selectGroup(g: CacheGroup): void {
    if (this.selectedId === g.questionId) return;
    this.selectedId = g.questionId;
    // Cleared rather than carried: the query ids belong to the question being
    // left, so the new question falls through to its first query.
    this.selectedQueryId = null;
    // And the open correction box goes with them, for the same reason: a draft is
    // typed against one query's SQL, and carried onto another question it would
    // offer to submit against a message it was never read against.
    this.cancelCorrection();
  }

  get selectedGroup(): CacheGroup | null {
    if (!this.selectedId) return null;
    return (
      this.listGroups.find((g) => g.questionId === this.selectedId) || null
    );
  }

  /**
   * The query the detail pane is showing.
   *
   * Resolved rather than stored, and re-resolved against the current group — so a
   * selected id left over from another question can never render its SQL under
   * this one's heading.
   *
   * With no explicit pick it opens on a query in the ACTIVE STATUS, not simply the
   * first one: a row in the Bad tab is there because one of its queries is bad, and
   * opening it on whichever attempt happens to be earliest would show a good or
   * ungraded query under a heading the tab says is bad. The other attempts stay one
   * click away in the query tabs, which is what keeps the history of a question
   * reviewable.
   */
  get activeQuery(): CacheQuery | null {
    const group = this.selectedGroup;
    if (!group?.queries.length) return null;

    const picked = group.queries.find(
      (q) => q.message_id === this.selectedQueryId
    );
    if (picked) return picked;

    const inTab = this.statusTab
      ? group.queries.find((q) => q.status === this.statusTab)
      : null;
    return inTab || group.queries[0];
  }

  get activeIndex(): number {
    const group = this.selectedGroup;
    const active = this.activeQuery;
    if (!group || !active) return -1;
    return group.queries.findIndex((q) => q.message_id === active.message_id);
  }

  selectQuery(messageId: string, ev?: Event): void {
    ev?.stopPropagation();
    if (messageId !== this.selectedQueryId) this.cancelCorrection();
    this.selectedQueryId = messageId;
  }

  /** The status dots a list row shows — one per query, in that query's colour,
   *  so a mixed question says so before it is opened. */
  statusDot(status: string): string {
    return CACHE_STATUS_META[status]?.dot || COLORS.pending;
  }

  statusLabel(status: string): string {
    return CACHE_STATUS_META[status]?.label || status;
  }

  statusBadgeClass(status: string): string {
    return CACHE_STATUS_META[status]?.badge || "badge-neutral";
  }

  /** "Aug 4, 1:32 PM" — enough to place a message in the queue without the row
   *  carrying a full timestamp. The footer prints the whole thing. */
  shortTime(ts: number): string {
    return ts ? moment(ts).format("MMM D, h:mm A") : "";
  }

  fullTime(ts: number): string {
    return ts ? moment(ts).format("DD MMM YYYY, HH:mm:ss") : "—";
  }

  // ---- copy ---------------------------------------------------------------
  /**
   * Copy a query's SQL.
   *
   * The confirmation is held in a field keyed by message_id rather than by
   * swapping the button's own innerHTML, so a re-render mid-timeout (a filter
   * change, another query's result landing) cannot leave a button stuck saying
   * "Copied".
   */
  copySql(q: CacheQuery, ev?: Event): void {
    ev?.stopPropagation();
    if (!q.sql) return;
    navigator.clipboard?.writeText(q.sql).catch(() => {
      /* a denied clipboard permission is not worth interrupting a review for */
    });
    this.copiedId = q.message_id;
    clearTimeout(this.copyTimer);
    this.copyTimer = setTimeout(() => {
      if (this.destroyed) return;
      this.copiedId = null;
      this.cdr.detectChanges();
    }, 1400);
  }

  copyCorrectedSql(q: CacheQuery, ev?: Event): void {
    ev?.stopPropagation();
    if (!q.correctedSql) return;
    navigator.clipboard?.writeText(q.correctedSql).catch(() => {
      /* a denied clipboard permission is not worth interrupting a review for */
    });
    this.copiedId = null;
    this.copiedCorrectedId = q.message_id;
    clearTimeout(this.copyTimer);
    this.copyTimer = setTimeout(() => {
      if (this.destroyed) return;
      this.copiedCorrectedId = null;
      this.cdr.detectChanges();
    }, 1400);
  }

  // ---- verdict (approve / reject) -----------------------------------------
  /** Is this query's verdict being posted right now? Holds both its buttons. */
  isSavingVerdict(messageId: string): boolean {
    return this.savingVerdictId === messageId;
  }

  /**
   * Is THIS button the one being posted? Both are held during a write, but only
   * the one that was clicked spins — two spinners would say two requests were out,
   * and a reviewer who clicked Approve should be able to see which way the verdict
   * they are waiting on went.
   *
   * A correction is posted as an approve (see submitCorrection), so the id and the
   * operation alone would spin the bar's Approve button for a click that happened in
   * the correction box. `savingCorrectionId` is what tells the two apart: while it
   * is set, the spinner belongs to the box's Submit and no button in the bar claims
   * it — they are all still held by isSavingVerdict().
   */
  isSavingOp(messageId: string, operation: CacheVerdict): boolean {
    return (
      !this.savingCorrectionId &&
      this.savingVerdictId === messageId &&
      this.savingVerdictOp === operation
    );
  }

  /** Is this query carrying a verdict that has been proposed but not signed off —
   *  GOOD or BAD (ready for review)? The two states the verdict bar has to report
   *  as themselves rather than as a settled verdict or as an ungraded message. */
  private isProposed(q: CacheQuery, status: string): boolean {
    return !q.approved && !q.rejected && q.status === status;
  }

  /**
   * The verdict bar's headline: what this query's verdict IS.
   *
   * FIVE readings, not three. Approved / Rejected are read off the same flags the
   * buttons' filled state is, so the sentence and the buttons cannot disagree; the
   * ready-for-review pair is named in CACHE_STATUS_META's own words — "Good (ready
   * for review)" — because that is neither a signed-off verdict nor an ungraded
   * message, and calling it "Approved" told a reviewer the one thing about it that
   * is not true. "Not reviewed" stays the wording for NO FEEDBACK, which is what
   * this line's question ("has anyone graded this") answers there.
   */
  verdictHeading(q: CacheQuery): string {
    if (q.approved) return "Approved";
    if (q.rejected) return "Rejected";
    if (
      this.isProposed(q, CACHE_STATUS.good) ||
      this.isProposed(q, CACHE_STATUS.bad)
    )
      return this.statusLabel(q.status);
    return "Not reviewed";
  }

  /**
   * The line under it: what the reviewer can do from here.
   *
   * The in-flight state comes first because it is the only one that is about right
   * now. On a settled query it names the OTHER button, which is the only way out of
   * a verdict — there is no withdraw (see setVerdict). On a proposed one it names
   * BOTH, because neither has been used yet: the point of this queue is the sign-off
   * that has not happened, and the bar is where a reviewer is told it is still owed.
   *
   * It does not claim who recorded an existing verdict. On this cache the status
   * IS the verdict, so a query can read as Approved with no reviewer column behind
   * it; `approved_by` / `rejected_by` are shown in the Message block below when the
   * feed carries them, which is the honest place for a name.
   */
  verdictSubtext(q: CacheQuery): string {
    if (this.isSavingVerdict(q.message_id)) return "Recording your review…";
    if (q.approved) return "Reject to change this verdict";
    if (q.rejected) return "Approve to change this verdict";
    if (this.isProposed(q, CACHE_STATUS.good))
      return "Not signed off yet — Approve to confirm it, or Reject to overturn it";
    if (this.isProposed(q, CACHE_STATUS.bad))
      return "Not signed off yet — Reject to confirm it, or Approve to overturn it";
    return "Files a review against this message, under your user id";
  }

  /** The mark in the verdict bar's chip, and the tone class that colours it. Kept
   *  as two small getters rather than one object so the template can bind each
   *  without a new object being allocated on every change-detection pass.
   *
   *  A proposed verdict keeps the glyph of the settled verdict it proposes — a tick
   *  is a tick whichever way it is going, and a fifth shape would be one more thing
   *  to learn — and is told apart by the tone: `-soft` rings the chip instead of
   *  filling it, the same outlined-until-decided language the two buttons use. */
  verdictMark(q: CacheQuery): string {
    if (q.approved || this.isProposed(q, CACHE_STATUS.good)) return "✓";
    if (q.rejected || this.isProposed(q, CACHE_STATUS.bad)) return "✕";
    return "—";
  }

  verdictTone(q: CacheQuery): string {
    if (q.approved) return "tone-good";
    if (q.rejected) return "tone-bad";
    if (this.isProposed(q, CACHE_STATUS.good)) return "tone-good-soft";
    if (this.isProposed(q, CACHE_STATUS.bad)) return "tone-bad-soft";
    return "tone-pending";
  }

  /**
   * Record a verdict on one query.
   *
   * Not optimistic: the flag flips only once the API has answered, because this
   * verdict is a change to the assistant's cache and a mark that showed as saved
   * while the write had failed would be a reviewer signing off on nothing. What
   * the click does immediately is hold the buttons and clear the last error.
   *
   * Re-posting the verdict a query already carries is allowed and deliberate — a
   * reviewer clicking Approve on an approved query is asserting it again, and the
   * endpoint takes the same operation either way. What is NOT here is a way to
   * withdraw one: `operation` is approve or reject, so the only way out of a
   * verdict is the other verdict.
   */
  setVerdict(group: CacheGroup, q: CacheQuery, operation: CacheVerdict): void {
    if (this.savingVerdictId) return; // one at a time
    this.savingVerdictId = q.message_id;
    this.savingVerdictOp = operation;
    delete this.verdictErrors[q.message_id];
    this.cdr.detectChanges();

    this.reqSubcription.push(
      this.cacheService
        .submitReview(this.apiContext, group, q, operation)
        .subscribe((res) => {
          this.savingVerdictId = null;
          this.savingVerdictOp = null;
          if (!res.ok) {
            this.verdictErrors[q.message_id] = res.error;
            this.cdr.detectChanges();
            return;
          }
          this.applyVerdict(group, q, operation);
          this.announceVerdict(operation);
        })
    );
  }

  // ---- correction ---------------------------------------------------------
  /** Is this query's correction box open? */
  isCorrecting(messageId: string): boolean {
    return this.correctionFor === messageId;
  }

  /** Is this query's correction being posted right now? */
  isSavingCorrection(messageId: string): boolean {
    return this.savingCorrectionId === messageId;
  }

  /**
   * Open the correction box on one query — or close it again, because the same
   * button is what a reviewer reaches for to back out of it.
   *
   * Seeded with the SQL as cached, not left blank: a correction is an edit to a
   * query that is nearly right, and retyping forty lines to fix a join condition is
   * not what the button is for. A reviewer who wants to start from nothing can still
   * clear the box.
   *
   * A box already open on ANOTHER query is taken over rather than refused: the
   * draft belongs to the query it was typed against, and two open boxes would leave
   * a reviewer able to submit the one they are not looking at.
   */
  toggleCorrection(q: CacheQuery): void {
    if (this.savingVerdictId) return; // a write is out on some query
    if (this.correctionFor === q.message_id) {
      this.cancelCorrection();
      return;
    }
    this.correctionFor = q.message_id;
    this.correctionDraft = q.sql || "";
    delete this.correctionErrors[q.message_id];
  }

  /** Close the box and drop the draft. Called by Cancel, and whenever the pane
   *  moves to another question or query — a draft is typed against one query's SQL
   *  and must not follow the reviewer onto the next one. */
  cancelCorrection(): void {
    if (!this.correctionFor) return;
    delete this.correctionErrors[this.correctionFor];
    this.correctionFor = null;
    this.correctionDraft = "";
  }

  /** Nothing typed, or nothing but whitespace — Submit stays held. The service
   *  trims what it posts, so a box of spaces would otherwise send an empty field. */
  get correctionEmpty(): boolean {
    return !this.correctionDraft.trim();
  }

  /**
   * Post a correction: the APPROVE call, with the corrected query attached.
   *
   * Deliberately not a third verdict. A reviewer filing a correction is saying the
   * message should be cached — with this query instead of the one it holds — so it
   * lands in APPROVED exactly as Approve does, through the same applyVerdict() that
   * moves the row to the Approved tab and recounts the pills. The only difference is
   * `corrected_sql` on the payload (see submitReview).
   *
   * Guarded by the same one-at-a-time rule as the buttons, and not optimistic for
   * the same reason: the box stays open with its draft intact until the API has
   * answered, so a refused write leaves the reviewer holding the query they typed
   * rather than having to type it again.
   */
  submitCorrection(group: CacheGroup, q: CacheQuery): void {
    if (this.savingVerdictId) return; // one at a time
    const corrected = this.correctionDraft.trim();
    if (!corrected) return;

    this.savingVerdictId = q.message_id;
    this.savingVerdictOp = "approve";
    this.savingCorrectionId = q.message_id;
    delete this.correctionErrors[q.message_id];
    delete this.verdictErrors[q.message_id];
    this.cdr.detectChanges();

    this.reqSubcription.push(
      this.cacheService
        .submitReview(this.apiContext, group, q, "approve", corrected)
        .subscribe((res) => {
          this.savingVerdictId = null;
          this.savingVerdictOp = null;
          this.savingCorrectionId = null;
          if (!res.ok) {
            this.correctionErrors[q.message_id] = res.error;
            this.cdr.detectChanges();
            return;
          }
          // Closed before the verdict is applied: applyVerdict() rebuilds the list
          // and can take this row out of the open tab, and a box left open would be
          // torn down mid-write with a draft still in it.
          this.correctionFor = null;
          this.correctionDraft = "";
          this.applyVerdict(group, q, "approve");
          this.announceVerdict("approve");
        })
    );
  }

  /**
   * The screen's own state, once a verdict has been accepted.
   *
   * Also written to this browser's store, so the mark survives the next read even
   * if the feed has not caught up with it — see readVerdicts().
   *
   * `status` moves with the verdict because the write that has just been accepted is
   * what settles it, and a query left reading Pending under an Approved chip would be
   * the screen contradicting itself until the next reload. It moves to APPROVED /
   * REJECTED — the tab named after the button that was pressed — rather than to the
   * READY FOR REVIEW pair: those two are what the FEED reports for queries graded
   * elsewhere, and a reviewer who has just clicked Approve should find the question
   * under Approved, which is also what the panel's hint promises them. `rawStatus` is
   * deliberately NOT touched — its job is to report the value as SENT, which is what
   * the badge's tooltip and the export column both say it is.
   *
   * The list IS rebuilt, which is what moves the row into the tab its new status
   * belongs to. Approving from the No feedback tab takes the question out of that
   * tab the moment the write lands and puts it under Approved — a graded query is no
   * longer backlog, and leaving it in place until the next reload had the tab
   * counts and the list disagreeing about the same question.
   *
   * A question is only dropped from the open tab once NONE of its queries match it
   * any more, because that is what buildList() filters on: grading one attempt of a
   * three-attempt question leaves the row where it is and just moves its "1 of 3"
   * on. So the row leaves exactly when the reviewer has finished with it under that
   * tab, which is also when the detail pane closing is the right outcome rather
   * than an interruption.
   *
   * On the All tab nothing is dropped at all — the rebuild is still needed there,
   * because `listedStatus` (the row's badge and its left stripe) is stamped by
   * buildList(), and without this the badge kept reading the pre-verdict status.
   *
   * The group's badge follows (worstStatus), and so do the tab counts and the KPI
   * figures, which count the filtered set.
   */
  private applyVerdict(
    group: CacheGroup,
    q: CacheQuery,
    operation: CacheVerdict
  ): void {
    q.approved = operation === "approve";
    q.rejected = operation === "reject";
    q.status =
      operation === "approve" ? CACHE_STATUS.approved : CACHE_STATUS.rejected;
    this.verdicts[q.message_id] = operation;
    this.cacheService.writeVerdicts(this.dashboardKey, this.verdicts);

    group.approvedCount = group.queries.filter((x) => x.approved).length;
    group.rejectedCount = group.queries.filter((x) => x.rejected).length;
    group.worstStatus = worstStatus(group.queries);

    // Read BEFORE the rebuild, so the notice below can tell "this row left the tab"
    // from "this row was never in it".
    const wasListed = this.listGroups.some(
      (g) => g.questionId === group.questionId
    );

    // All three, in this order. The pill counts and the per-status cards have just
    // had a query move between statuses, so both need recounting; buildList() is what
    // actually relocates the row — it re-filters against the open tab and re-stamps
    // every row's `listedStatus`. It runs last because it reads the tab set the other
    // two have just settled.
    //
    // Total is not among the figures that can move here: the query stays in view, it
    // only changes which status it counts under, so the row's sum is the same before
    // and after. It is recomputed all the same because it comes off buildKpis()' one
    // pass, which is what the five status cards need.
    this.buildTabs();
    this.buildKpis();
    this.buildList();
    this.buildExcelRows();

    const stillListed = this.listGroups.some(
      (g) => g.questionId === group.questionId
    );
    if (wasListed && !stillListed) this.raiseMovedNotice(operation, q.status);

    this.cdr.detectChanges();
  }

  /** Say where a row went, and take the line down again a few seconds later. Long
   *  enough to be read after a click the reviewer made deliberately, short enough
   *  that it is gone before the next verdict. */
  private raiseMovedNotice(operation: CacheVerdict, status: string): void {
    const verb = operation === "approve" ? "Approved" : "Rejected";
    const tab = CACHE_STATUS_META[status]?.short || status;
    this.movedNotice = `${verb} — moved to the ${tab} tab.`;
    clearTimeout(this.movedTimer);
    this.movedTimer = setTimeout(() => {
      if (this.destroyed) return;
      this.movedNotice = null;
      this.cdr.detectChanges();
    }, 4000);
  }

  /**
   * Confirm an accepted verdict in a snackbar.
   *
   * Called only from the success branch of setVerdict, so a snackbar on this screen
   * always means the API took the write — a failed one stays on the row it belongs
   * to, as the "Not recorded" line under its buttons, where the reviewer can see
   * WHICH query did not save. A dismissable bar at the bottom of the window could
   * not say that.
   *
   * Names the status the query has just landed in, which is the tab it has just moved
   * to (applyVerdict), so the confirmation and the relocation read as one event.
   */
  private announceVerdict(operation: CacheVerdict): void {
    const status =
      operation === "approve" ? CACHE_STATUS.approved : CACHE_STATUS.rejected;
    const verb = CACHE_STATUS_META[status]?.short || status;
    this.dialogService.SnackBar(`Review recorded — ${verb}.`, "OK");
  }

  /** Drop the notice the moment the reviewer moves somewhere else — it describes
   *  one list, and it should not outlive it. */
  private clearMovedNotice(): void {
    if (!this.movedNotice) return;
    clearTimeout(this.movedTimer);
    this.movedNotice = null;
  }

  // ---- export -------------------------------------------------------------
  /** One row per query in view — see reviewExcelConfig. Refilled in place so
   *  icon-chart keeps the array reference it was handed. */
  private buildExcelRows(): void {
    const rows = this.viewGroups.flatMap((g) =>
      g.queries.map((q) => ({
        question: g.promptText,
        project: g.project,
        brand_key: g.brandKey,
        team_name: g.teamName,
        schema_version: g.schemaVersion,
        cache_status: q.rawStatus || q.status,
        // The SIGN-OFF, which is not the Cache Status column beside it: that
        // reports the value as sent, this reports how far through the queue the
        // query is. A ready-for-review row is "Awaiting sign-off" and not
        // "Approved" — which way it is proposed is in the column to its left, and
        // an export is exactly where a proposed verdict counted as a settled one
        // would go unnoticed.
        review: q.approved
          ? "Approved"
          : q.rejected
          ? "Rejected"
          : q.status === CACHE_STATUS.good || q.status === CACHE_STATUS.bad
          ? "Awaiting sign-off"
          : "—",
        ingested: q.ingested ? "Y" : "N",
        total_tokens: q.totalTokens,
        llm_time: q.llmTime,
        db_time: q.dbTime,
        message_timestamp: this.fullTime(q.timestamp),
        conversation_id: g.conversationId,
        message_id: q.message_id,
        sql_query: q.sql,
      }))
    );
    this.reviewExcelRows.length = 0;
    this.reviewExcelRows.push(...rows);
  }

  /**
   * icon-chart's Fullscreen icon. Read back off the DOM rather than trusting the
   * emitted flag: its openFullscreen() emits `true` from a timer whether or not
   * requestFullscreen actually succeeded.
   */
  onReviewFullscreen(): void {
    this.syncFullscreenFromDom();
  }

  // Fullscreen can also start or end without the header icon being involved —
  // Esc, F11, the browser's own affordance. icon-chart listens on its own host,
  // and the element that goes fullscreen is an ancestor of it, so the event never
  // reaches it.
  @HostListener("document:fullscreenchange")
  @HostListener("document:webkitfullscreenchange")
  @HostListener("document:mozfullscreenchange")
  @HostListener("document:MSFullscreenChange")
  onDocumentFullscreenChange(): void {
    this.syncFullscreenFromDom();
  }

  private syncFullscreenFromDom(): void {
    const el: any = document.fullscreenElement;
    const open = !!el && !!el.closest?.(".review-panel");
    if (open !== this.reviewFullscreen) {
      this.reviewFullscreen = open;
      this.cdr.detectChanges();
    }
  }

  // ============================================================= persistence
  private saveFilterState(): void {
    if (!this.stateReady) return;
    try {
      localStorage.setItem(
        this.storageKey,
        JSON.stringify({
          // Only the chips the user has actually narrowed. A chip sitting on all
          // of its values is left out entirely, so the next load puts it back on
          // all of ITS values — which, after a date change, is a different list.
          // Writing the values down instead would pin the chip to the previous
          // window's options, and a wider window's new projects and teams would
          // be filtered out by a selection nobody made.
          filters: this.filters.reduce((acc: Record<string, string[]>, f) => {
            if (this.isPartial(f)) acc[f.key] = [...f.applied];
            return acc;
          }, {}),
          fromDate: this.fromDate,
          toDate: this.toDate,
          searchText: this.searchText,
          statusTab: this.statusTab,
        })
      );
    } catch {
      /* storage unavailable (private mode / quota) — ignore */
    }
  }

  /** Forget the persisted selection, so the next build defaults everything. Both
   *  the stored copy and the in-memory one, or onDataLoaded() would read the
   *  entry back and restore what was just discarded. */
  private clearFilterState(): void {
    this.savedState = null;
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
      /* storage unavailable — ignore */
    }
  }

  private readSavedState(): any {
    try {
      const raw = localStorage.getItem(this.storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  /**
   * Put back what the last visit had selected, validated against what is
   * actually loaded now.
   *
   * Every restore is checked rather than trusted: this is parsed localStorage,
   * and a saved value the current response has no rows for would be a filter
   * matching nothing — a screen that comes up empty for a reason nothing on it
   * explains.
   *
   * The date range is not restored here. It is a request parameter, so it is
   * settled before the read (initRequestRange) — by the time this runs the
   * response has already been fetched for the saved window, and re-applying it
   * would either be a no-op or, if it were validated against the rows the way the
   * chips are, would move the window away from the one that was asked for.
   */
  private restoreFilterState(): void {
    const state = this.savedState;
    if (!state) return;

    if (state.filters) {
      this.filters.forEach((f) => {
        const saved = state.filters[f.key];
        // No entry means the chip was not narrowed when it was saved — see
        // saveFilterState. It keeps the default buildFilters() just gave it, i.e.
        // every value the response that just arrived holds.
        if (!Array.isArray(saved)) return;
        const valid = saved.filter((v: string) => f.options.includes(v));
        // A selection that has gone stale falls back to the chip's default, so
        // the chip shows everything again rather than hiding data.
        f.applied = new Set(valid.length ? valid : f.defaultApplied);
        f.checked = [...f.applied];
      });
    }

    if (typeof state.searchText === "string") {
      this.searchText = state.searchText;
    }

    // The status tab, validated the way the chips are: only a status the strip will
    // actually offer, so a value left over from an older build cannot open a tab
    // that is not there. Anything else falls back to All.
    if (typeof state.statusTab === "string") {
      const offered = new Set<string>(CACHE_STATUS_TAB_ORDER);
      this.groups.forEach((g) => g.queries.forEach((q) => offered.add(q.status)));
      this.statusTab = offered.has(state.statusTab) ? state.statusTab : null;
    }
  }

  // ================================================================= chrome
  /** Load a card's help text into the shared popover — the same affordance the
   *  real KPI cards carry (card2kpi), on the same click that opens the menu. */
  showCardHelp(entry: HelpEntry | null): void {
    this.cardHelp = entry;
  }

  /**
   * Close an open badge dropdown or the date popup on any click outside them.
   *
   * The triggers and the menus stopPropagation, so a click only reaches the
   * document when it is genuinely outside — which works regardless of z-index
   * stacking, where the transparent backdrops cannot (the filter bar sits above
   * them).
   */
  @HostListener("document:click")
  onDocumentClick(): void {
    if (this.openKey || this.dateMenuOpen) {
      this.openKey = null;
      this.dateMenuOpen = false;
    }
  }

  @HostListener("window:resize")
  onWindowResize(): void {
    this.updateBadgeOverflow();
  }

  previousPage(): void {
    this.navigationService.goBack();
  }
}
